import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";

import { GetPurchaseBillReturnById, UpdatePurchaseBillReturn } from "../../../../api/medicalStore/transaction/purchaseBillReturnAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetOpdEntries } from "../../../../api/opd/opdEntryAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetSaleBillById, UpdateSaleBill } from "../../../../api/medicalStore/transaction/saleBillAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  docType: yup.string().required(),
  billNo: yup.number().required(),
  date: yup.date().required(),
  time: yup.string().required(),
  customerType: yup.string().required(),
  opdEntryId: yup.string().nullable(),
  ipdEntryId: yup.string().nullable(),
  departmentId: yup.string().required(),
  customerName: yup.string().required(),
  consultantId: yup.string().required(),
  organisation: yup.string().required(),

  totalQty: yup.number().required(),
  grossAmount: yup.number().required(),
  gst: yup.number().required(),
  discountPercentage: yup.number().required(),
  discountAmount: yup.number().required(),
  totalAmount: yup.number().required(),
  discountPercentageAfterGst: yup.number().required(),
  discountAmountAfterGst: yup.number().required(),
  roundOff: yup.number().required(),
  netAmount: yup.number().required(),
  remark: yup.string().optional(),
  againstAccount: yup.string().optional(),

  medicineDetails: yup.array().of(
    yup.object().shape({
      categoryId: yup.string().required(),
      productId: yup.string().required(),
      expiryDate: yup.date().required(),
      batchNo: yup.number().required(),
      quantity: yup.number().required(),
      mrp: yup.number().required(),
      saleRate: yup.number().required(),
      amount: yup.number().required(),
      discountPercentage: yup.number().required(),
      discountAmount: yup.number().required(),
      gstPercentage: yup.number().required(),
      gstAmount: yup.number().required(),
      totalAmount: yup.number().required()
    })
  )
});

const EditSaleBillEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const [departments, setDepartments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [opdEntries, setOpdEntries] = useState([]);
    const [ipdEntries, setIpdEntries] = useState([]);
    const [consultants, setConsultants] = useState([]);
  
    const {
      register,
      control,
      setValue,
      getValues,
      handleSubmit,
      formState: { errors }
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        docType: "Sale Bill Entry",
        customerType: "OPD",
        medicineDetails: [
          {
            categoryId: "",
            productId: "",
            expiryDate: "",
            batchNo: "",
            quantity: "",
            mrp: "",
            saleRate: "",
            amount: "",
            discountPercentage: "",
            discountAmount: "",
            gstPercentage: "",
            gstAmount: "",
            totalAmount: ""
          }
        ]
      }
    });
  
    const { fields, append, remove } = useFieldArray({
      control,
      name: "medicineDetails"
    });

  const calculateRow = index => {
      const qty = Number(getValues(`medicineDetails.${index}.quantity`) || 0);
      const rate = Number(getValues(`medicineDetails.${index}.saleRate`) || 0);
      const discPct = Number(
        getValues(`medicineDetails.${index}.discountPercentage`) || 0
      );
      const gstPct = Number(
        getValues(`medicineDetails.${index}.gstPercentage`) || 0
      );
  
      const amount = qty * rate;
      const discountAmount = (amount * discPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstPct) / 100;
      const totalAmount = taxableAmount + gstAmount;
  
      setValue(`medicineDetails.${index}.amount`, amount.toFixed(2));
      setValue(`medicineDetails.${index}.discountAmount`, discountAmount.toFixed(2));
      setValue(`medicineDetails.${index}.gstAmount`, gstAmount.toFixed(2));
      setValue(`medicineDetails.${index}.totalAmount`, totalAmount.toFixed(2));
    };
  
    const watched = useWatch({ control, name: "medicineDetails" });
  
    const discountPercentageAfterGst = useWatch({
      control,
      name: "discountPercentageAfterGst"
    });
  
    const roundOff = useWatch({
      control,
      name: "roundOff"
    });
  
    useEffect(() => {
      let totalQty = 0;
      let grossAmount = 0;
      let gst = 0;
      let discountAmount = 0;
  
      watched?.forEach(item => {
        totalQty += Number(item.quantity || 0);
        grossAmount += Number(item.amount || 0);
        gst += Number(item.gstAmount || 0);
        discountAmount += Number(item.discountAmount || 0);
      });
  
      const totalAmount = grossAmount - discountAmount + gst;
  
      const afterGstDiscPct = Number(discountPercentageAfterGst || 0);
      const discountAmountAfterGst =
        (totalAmount * afterGstDiscPct) / 100;
  
      const netAmount =
        totalAmount -
        discountAmountAfterGst;
  
      setValue("totalQty", totalQty);
      setValue("grossAmount", grossAmount.toFixed(2));
      setValue("gst", gst.toFixed(2));
      setValue("discountAmount", discountAmount.toFixed(2));
      setValue("totalAmount", totalAmount.toFixed(2));
      setValue("discountAmountAfterGst", discountAmountAfterGst.toFixed(2));
      setValue("netAmount", netAmount.toFixed(Number(roundOff || 0)));
  
    }, [
      watched,
      discountPercentageAfterGst,
      roundOff,
      setValue
    ]);

  const fetchData = async () => {
    try {
      const response = await GetSaleBillById(id); // Make sure login function returns token
      
      if (response.success) {
        const data = response.data;
        // ================= SIMPLE FIELDS =================
        Object.keys(data).forEach(key => {
          if (key === "productDetails") return;

          // handle date fields if any in future
          if (data[key] instanceof Date || typeof data[key] === "string") {
            if (key.includes("date") || key.includes("billDate") && data[key]) {
              setValue(key, data[key].split("T")[0]);
              return;
            }
          }

          setValue(key, data[key]);
        });

        // ================= MEDICINE DETAILS (ARRAY) =================
        if (Array.isArray(data.medicineDetails)) {
          data.medicineDetails.forEach((item, index) => {
            Object.keys(item).forEach(field => {
              if (field === "expiryDate" && item[field]) {
                setValue(
                  `medicineDetails.${index}.expiryDate`,
                  item[field].split("T")[0]
                );
              } else {
                setValue(
                  `medicineDetails.${index}.${field}`,
                  item[field]
                );
              }
            });
          });
        }
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    // Fetch existing data
    const loadAllData = async () => {
      try {
        const [
          departmentRes,
          categoryRes,
          itemRes,
          opdEntries,
          ipdEntries,
          consultants
        ] = await Promise.all([
          GetDepartments(),
          GetCategoriesMedicalStore(),
          GetItems(),
          GetOpdEntries(),
          GetIpdEntries(),
          GetConsultants()
        ]);

        if (departmentRes.success) {
          setDepartments(departmentRes.data);
        } else {
          showToast("error", departmentRes.message);
        }

        if (categoryRes.success) {
          setCategories(categoryRes.data);
        } else {
          showToast("error", categoryRes.message);
        }

        if (itemRes.success) {
          setItems(itemRes.data);
        } else {
          showToast("error", itemRes.message);
        }

        if (opdEntries.success) {
          setOpdEntries(opdEntries.data);
        } else {
          showToast("error", opdEntries.message);
        }

        if (ipdEntries.success) {
          setIpdEntries(ipdEntries.data);
        } else {
          showToast("error", ipdEntries.message);
        }

        if (consultants.success) {
          setConsultants(consultants.data);
        } else {
          showToast("error", consultants.message);
        }

        // ✅ CALL AFTER ALL ABOVE ARE DONE
        fetchData();
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };
    loadAllData();
  }, [id]);


  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateSaleBill(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Edit Sale Bill Entry" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Sale Bill Entry</h5>
            <Link
              to={`/medical-store/transaction/sale-bill-entry/list`}
            >
              <button className="btn btn-primary" id="list">
                List Sale Bill
              </button>
              <UncontrolledTooltip target="list">
                Back to List
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
            <div className="card-body">

              {/* ================= HEADER ================= */}
              <div className="row">

                <div className="col-3">
                  <label>Document Type</label>
                  <input className="form-control" {...register("docType")} />
                  {errors.docType && <span className="text-danger">{errors.docType.message}</span>}
                </div>

                <div className="col-3">
                  <label>Bill No</label>
                  <input type="number" className="form-control" {...register("billNo")} />
                  {errors.billNo && <span className="text-danger">{errors.billNo.message}</span>}
                </div>

                <div className="col-3">
                  <label>Date</label>
                  <input type="date" className="form-control" {...register("date")} />
                  {errors.date && <span className="text-danger">{errors.date.message}</span>}
                </div>

                <div className="col-3">
                  <label>Time</label>
                  <input type="time" className="form-control" {...register("time")} />
                  {errors.time && <span className="text-danger">{errors.time.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Customer Type</label>
                  <select className="form-control" {...register("customerType")} >
                    <option value="">Select</option>
                    <option value="General">General</option>
                    <option value="Walkin">Walkin</option>
                    <option value="IPD">IPD</option>
                    <option value="OPD">OPD</option>
                  </select>
                  {errors.customerType && <span className="text-danger">{errors.customerType.message}</span>}
                </div>
                
                <div className="col-3 mt-2">
                  <label>OPD Entry No</label>
                  <select className="form-control" {...register("opdEntryId")} >
                    <option value="">Select</option>
                    {opdEntries.map(d => (
                      <option key={d._id} value={d._id}>{d.opdNo}</option>
                    ))}
                  </select>
                  {errors.opdEntryId && <span className="text-danger">{errors.opdEntryId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>IPD Entry No</label>
                  <select className="form-control" {...register("ipdEntryId")} >
                    <option value="">Select</option>
                    {ipdEntries.map(d => (
                      <option key={d._id} value={d._id}>{d.opdNo}</option>
                    ))}
                  </select>
                  {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Department</label>
                  <select className="form-control" {...register("departmentId")}>
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                  {errors.departmentId && <span className="text-danger">{errors.departmentId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Customer Name</label>
                  <input className="form-control" {...register("customerName")} />
                  {errors.customerName && <span className="text-danger">{errors.customerName.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Consultant</label>
                  <select className="form-control" {...register("consultantId")} >
                    <option value="">Select</option>
                    {consultants.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                  {errors.consultantId && <span className="text-danger">{errors.consultantId.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Organisation</label>
                  <input className="form-control" {...register("organisation")} />
                  {errors.organisation && <span className="text-danger">{errors.organisation.message}</span>}
                </div>

              </div>

              <hr />
              <h6>Medicine Details</h6>

              {fields.map((item, index) => (
                <div className="row border p-2 mb-2" key={item.id}>

                  <div className="col-2">
                    <label>Category</label>
                    <select className="form-control" {...register(`medicineDetails.${index}.categoryId`)}>
                      <option value="">Select</option>
                      {categories.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                    {errors.medicineDetails?.[index]?.categoryId &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].categoryId.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Product</label>
                    <select className="form-control" {...register(`medicineDetails.${index}.productId`)}>
                      <option value="">Select</option>
                      {items.map(p => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                    {errors.medicineDetails?.[index]?.productId &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].productId.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Expiry Date</label>
                    <input type="date" className="form-control"
                      {...register(`medicineDetails.${index}.expiryDate`)} />
                    {errors.medicineDetails?.[index]?.expiryDate &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].expiryDate.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Batch No</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.batchNo`)} />
                    {errors.medicineDetails?.[index]?.batchNo &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].batchNo.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>Quantity</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.quantity`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.quantity &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].quantity.message}
                      </span>}
                  </div>

                  <div className="col-2">
                    <label>MRP</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.mrp`)} />
                    {errors.medicineDetails?.[index]?.mrp &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].mrp.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Sale Rate</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.saleRate`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.saleRate &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].saleRate.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.amount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>Discount %</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.discountPercentage`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.discountPercentage &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].discountPercentage.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>Discount Amount</label>
                    <input className="form-control" readOnly
                      {...register(`medicineDetails.${index}.discountAmount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>GST %</label>
                    <input type="number" step="any" className="form-control"
                      {...register(`medicineDetails.${index}.gstPercentage`, {
                        onChange: () => calculateRow(index)
                      })} />
                    {errors.medicineDetails?.[index]?.gstPercentage &&
                      <span className="text-danger">
                        {errors.medicineDetails[index].gstPercentage.message}
                      </span>}
                  </div>

                  <div className="col-2 mt-2">
                    <label>GST Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.gstAmount`)} />
                  </div>

                  <div className="col-2 mt-2">
                    <label>Total Amount</label>
                    <input type="number" step="any" className="form-control" readOnly
                      {...register(`medicineDetails.${index}.totalAmount`)} />
                  </div>

                  <div className="col-1 mt-4">
                    <button type="button" className="btn btn-danger" onClick={() => remove(index)}>✕</button>
                  </div>

                </div>
              ))}

              <button type="button" className="btn btn-secondary" onClick={() => append({})}>
                + Add Medicine
              </button>

              <hr />

              {/* ================= SUMMARY ================= */}
              <div className="row">

                <div className="col-3">
                  <label>Total Qty</label>
                  <input type="number" step="any" className="form-control" {...register("totalQty")} />
                  {errors.totalQty && <span className="text-danger">{errors.totalQty.message}</span>}
                </div>

                <div className="col-3">
                  <label>Gross Amount</label>
                  <input type="number" step="any" className="form-control" {...register("grossAmount")} />
                  {errors.grossAmount && <span className="text-danger">{errors.grossAmount.message}</span>}
                </div>

                <div className="col-3">
                  <label>GST</label>
                  <input type="number" step="any" className="form-control" {...register("gst")} />
                  {errors.gst && <span className="text-danger">{errors.gst.message}</span>}
                </div>

                <div className="col-3">
                  <label>Discount %</label>
                  <input type="number" step="any" className="form-control" {...register("discountPercentage")} />
                  {errors.discountPercentage && <span className="text-danger">{errors.discountPercentage.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Discount Amount</label>
                  <input type="number" step="any" className="form-control" {...register("discountAmount")} />
                  {errors.discountAmount && <span className="text-danger">{errors.discountAmount.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Total Amount</label>
                  <input type="number" step="any" className="form-control" {...register("totalAmount")} />
                  {errors.totalAmount && <span className="text-danger">{errors.totalAmount.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Discount % After GST</label>
                  <input type="number" step="any" className="form-control" {...register("discountPercentageAfterGst")} />
                  {errors.discountPercentageAfterGst &&
                    <span className="text-danger">{errors.discountPercentageAfterGst.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Discount Amount After GST</label>
                  <input type="number" step="any" className="form-control" {...register("discountAmountAfterGst")} />
                  {errors.discountAmountAfterGst &&
                    <span className="text-danger">{errors.discountAmountAfterGst.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Round Off</label>
                  <input type="number" step="any" className="form-control" {...register("roundOff")} />
                  {errors.roundOff && <span className="text-danger">{errors.roundOff.message}</span>}
                </div>

                <div className="col-3 mt-2">
                  <label>Net Amount</label>
                  <input type="number" step="any" className="form-control" {...register("netAmount")} />
                  {errors.netAmount && <span className="text-danger">{errors.netAmount.message}</span>}
                </div>

                <div className="col-6 mt-2">
                  <label>Remark</label>
                  <textarea className="form-control" {...register("remark")} />
                </div>

                <div className="col-3 mt-2">
                  <label>Against Account</label>
                  <input className="form-control" {...register("againstAccount")} />
                </div>

              </div>

            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary" type="submit">
                Submit
              </button>
            </div>
          </form>


        </div>
      </div>
    </Fragment>
  );
};

export default EditSaleBillEntry;
